
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormInput, CFormLabel, CButton, CSpinner, CAlert
} from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const CustomerInputs = ({
  onSubmit,
  onCancel,
  isLoading = false,
  submitResponse = null,
  initialData = null,
  isEdit = false
}) => {
  const [formData, setFormData] = useState({
    CustomerName: '',
    CustomerDescription: '',
    Address: '',
    CepTel: '',
    Telefon: '',
    Contact: '',
    Email: ''
  });
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData) {
      setFormData({
        CustomerName: initialData.CustomerName || '',
        CustomerDescription: initialData.CustomerDescription || '',
        Address: initialData.Address || '',
        CepTel: initialData.CepTel || '',
        Telefon: initialData.Telefon || '',
        Contact: initialData.Contact || '',
        Email: initialData.Email || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setTimeout(() => {
        if (!isEdit) {
          setFormData({
            CustomerName: '',
            CustomerDescription: '',
            Address: '',
            CepTel: '',
            Telefon: '',
            Contact: '',
            Email: ''
          });
        }
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.CustomerName.trim()) newErrors.CustomerName = t('customerNameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;
      if (!userId || userId === 'undefined') {
        setErrors({ general: 'User session not found. Please login again.' });
        return;
      }
      const submitData = {
        SessionUserId: String(userId),
        CustomerName: formData.CustomerName.trim(),
        CustomerDescription: formData.CustomerDescription.trim(),
        Address: formData.Address.trim(),
        CepTel: formData.CepTel.trim(),
        Telefon: formData.Telefon.trim(),
        Contact: formData.Contact.trim(),
        Email: formData.Email.trim()
      };
      if (isEdit && initialData?.CustomerId) {
        submitData.CustomerId = initialData.CustomerId;
      }
      onSubmit(submitData);
    }
  };

  const handleCancel = () => {
    if (!isEdit) {
      setFormData({
        CustomerName: '',
        CustomerDescription: '',
        Address: '',
        CepTel: '',
        Telefon: '',
        Contact: '',
        Email: ''
      });
    }
    setErrors({});
    onCancel();
  };

  const getButtonText = () => {
    if (isLoading) {
      return isEdit ? t('updating') : t('creating');
    }
    return isEdit ? t('updateCustomer') : t('createCustomer');
  };

  return (
    <div>
      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}
      {submitResponse?.Result?.ErrorCode !== 0 && submitResponse?.Result?.ErrorDescription && (
        <CAlert color="danger">{submitResponse.Result.ErrorDescription}</CAlert>
      )}
      <CForm onSubmit={handleSubmit} noValidate>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="CustomerName">{t('customerName')}</CFormLabel>
            <CFormInput
              type="text"
              id="CustomerName"
              name="CustomerName"
              value={formData.CustomerName}
              onChange={handleInputChange}
              invalid={!!errors.CustomerName}
              placeholder={t('enterCustomerName')}
              disabled={isLoading}
            />
            {errors.CustomerName && <div className="invalid-feedback d-block">{errors.CustomerName}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="CustomerDescription">{t('customerDescription')}</CFormLabel>
            <CFormInput
              type="text"
              id="CustomerDescription"
              name="CustomerDescription"
              value={formData.CustomerDescription}
              onChange={handleInputChange}
              invalid={!!errors.CustomerDescription}
              placeholder={t('enterCustomerDescription')}
              disabled={isLoading}
            />
            {errors.CustomerDescription && <div className="invalid-feedback d-block">{errors.CustomerDescription}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="Address">{t('address')}</CFormLabel>
            <CFormInput
              type="text"
              id="Address"
              name="Address"
              value={formData.Address}
              onChange={handleInputChange}
              invalid={!!errors.Address}
              placeholder={t('enterAddress')}
              disabled={isLoading}
            />
            {errors.Address && <div className="invalid-feedback d-block">{errors.Address}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="CepTel">{t('mobilePhone')}</CFormLabel>
            <CFormInput
              type="text"
              id="CepTel"
              name="CepTel"
              value={formData.CepTel}
              onChange={handleInputChange}
              invalid={!!errors.CepTel}
              placeholder={t('enterCepTel')}
              disabled={isLoading}
            />
            {errors.CepTel && <div className="invalid-feedback d-block">{errors.CepTel}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="Telefon">{t('userPhone')}</CFormLabel>
            <CFormInput
              type="text"
              id="Telefon"
              name="Telefon"
              value={formData.Telefon}
              onChange={handleInputChange}
              invalid={!!errors.Telefon}
              placeholder={t('enterTelefon')}
              disabled={isLoading}
            />
            {errors.Telefon && <div className="invalid-feedback d-block">{errors.Telefon}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="Contact">{t('contact')}</CFormLabel>
            <CFormInput
              type="text"
              id="Contact"
              name="Contact"
              value={formData.Contact}
              onChange={handleInputChange}
              invalid={!!errors.Contact}
              placeholder={t('enterContact')}
              disabled={isLoading}
            />
            {errors.Contact && <div className="invalid-feedback d-block">{errors.Contact}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="Email">{t('email')}</CFormLabel>
            <CFormInput
              type="email"
              id="Email"
              name="Email"
              value={formData.Email}
              onChange={handleInputChange}
              invalid={!!errors.Email}
              placeholder={t('enterEmail')}
              disabled={isLoading}
            />
            {errors.Email && <div className="invalid-feedback d-block">{errors.Email}</div>}
          </CCol>
        </CRow>
        <CRow>
          <CCol md={12} className="d-flex gap-2">
            <CButton color="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {getButtonText()}
                </>
              )}
            </CButton>
            <CButton color="secondary" variant="outline" onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" />
              {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

CustomerInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default CustomerInputs; 